import { z } from "zod/v4";

/**
 * Admin user edit form validation schema.
 *
 * Email and phone number are required and cannot be cleared from this form. Both rules mirror
 * `UpdateUserCommandValidator` on the backend, and the phone rule mirrors `PhoneNumberRules.cs`;
 * a change there needs the same change here.
 */

/** Something@domain.tld with no spaces — the same pattern the backend validator matches. */
const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** + then a non-zero digit, or digits only. Length is checked separately. */
const PHONE_FORMAT_REGEX = /^(\+[1-9]\d*|\d+)$/;
export const PHONE_MIN_DIGITS = 10;
export const PHONE_MAX_DIGITS = 15; // E.164 limit

export const USER_EDIT_ROLES = [
  "User",
  "SuperAdmin",
  "BillingAdmin",
  "SupportAdmin",
] as const;

export const userEditSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, "First name is required")
    .max(100, "First name must not exceed 100 characters"),
  lastName: z
    .string()
    .trim()
    .max(100, "Last name must not exceed 100 characters"),
  email: z
    .string()
    .trim()
    // abort: an empty box reports "required" alone, not also "invalid".
    .min(1, { message: "Email is required", abort: true })
    .max(256, "Email must not exceed 256 characters")
    .regex(EMAIL_REGEX, "Invalid email address"),
  phoneNumber: z
    .string()
    .trim()
    .superRefine((value, ctx) => {
      if (!value) {
        ctx.addIssue({ code: "custom", message: "Phone number is required" });
        return;
      }
      if (!PHONE_FORMAT_REGEX.test(value)) {
        ctx.addIssue({
          code: "custom",
          message: "Use digits only, optionally starting with + and a country code",
        });
        return;
      }
      const digitCount = value.replace("+", "").length;
      if (digitCount < PHONE_MIN_DIGITS) {
        ctx.addIssue({
          code: "custom",
          message: `Phone number must have at least ${PHONE_MIN_DIGITS} digits`,
        });
      } else if (digitCount > PHONE_MAX_DIGITS) {
        ctx.addIssue({
          code: "custom",
          message: `Phone number must not exceed ${PHONE_MAX_DIGITS} digits`,
        });
      }
    }),
  role: z.enum(USER_EDIT_ROLES),
});

export type UserEditFormData = z.infer<typeof userEditSchema>;
